import { CubePresenter, FlipIntent, Logger, NOOP_VOID, ScopeSlot } from 'src/3rds/cube'
import { MainPresenter } from 'src/main/Main.presenter'
import { AppStorage } from 'src/utils'
import { ServiceError } from 'src/utils/ServiceError'
import { CampanhaKeys } from './Campanha.keys'
import { Aba, Bloco, CampanhaScope, Parceiro } from './Campanha.scopes'
import { CampanhaService, MIMES_ACEITOS, TAMANHO_MAXIMO_BYTES } from './Campanha.service'

const LOG = Logger.get('CampanhaPresenter')
const service = CampanhaService.singleton()
const appStorage = AppStorage.singleton()

// Enquanto a campanha entrega, a aba de situação repergunta neste intervalo.
const POLLING_MS = 5000

// A base tem milhares de parceiros: o filtro é do servidor, depois que o operador para de digitar.
const DEBOUNCE_BUSCA_MS = 300

// Espera pela confirmação de entrega do teste (a fila leva ~1s, mas pode estar com trabalho).
const ESPERA_TESTE_MS = 3000
const ESPERA_TESTE_MAX_TENTATIVAS = 10

export const ROLES_AUTORIZADOS = new Set(['admin', 'manager'])

export function podeGerenciarCampanhas() {
    return ROLES_AUTORIZADOS.has(`${appStorage.userRole ?? ''}`)
}

/** O template da campanha é um template da conta, e cada envio dele é pago: só admin, como no servidor. */
export function podeGerirTemplateDaCampanha() {
    return appStorage.userRole === 'admin'
}

export class CampanhaPresenter extends CubePresenter<MainPresenter, CampanhaScope> {
    private parentSlot: ScopeSlot = NOOP_VOID
    private pollingTimer?: ReturnType<typeof setTimeout>
    private buscaParceiroTimer?: ReturnType<typeof setTimeout>
    private testeTimer?: ReturnType<typeof setTimeout>
    /** objectURLs das imagens já exibidas, para liberar ao sair. */
    private urlsCriadas: string[] = []

    constructor(app: MainPresenter) {
        super(app, new CampanhaScope())
    }

    public override release() {
        this.#pararPolling()
        this.#pararEsperaDoTeste()
        this.#cancelarBuscaAgendada()
        this.urlsCriadas.forEach((url) => URL.revokeObjectURL(url))
        this.urlsCriadas = []
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new CampanhaKeys(this.app, intent)

        if (initialization) {
            if (!podeGerenciarCampanhas()) {
                this.app.alert('warning', 'Acesso restrito', 'As campanhas são restritas a administradores.')
                keys.allow = false
                return keys.allow
            }

            this.parentSlot = keys.parentSlot
            await this.#initialize()
        }

        if (keys.newsId && keys.newsId !== this.scope.campanha?.newsId) {
            await this.#abrirCampanha(keys.newsId)
        }

        this.parentSlot(this.scope)

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new CampanhaKeys(this.app, intent)
        keys.newsId = this.scope.campanha?.newsId ?? ''
    }

    async #initialize() {
        LOG.debug('initialize')

        this.scope.onTrocarAba = this.action(this.#handleTrocarAba, this)
        this.scope.onNovaCampanha = this.action(this.#handleNovaCampanha, this)
        this.scope.onAbrirCampanha = this.action(this.#handleAbrirCampanha, this)
        this.scope.onNewsIdChange = this.action(this.#handleNewsIdChange, this)
        this.scope.onAdicionarTexto = this.action(this.#handleAdicionarTexto, this)
        this.scope.onSelecionarImagem = this.action(this.#handleSelecionarImagem, this)
        this.scope.onAlterarBloco = this.action(this.#handleAlterarBloco, this)
        this.scope.onRemoverBloco = this.action(this.#handleRemoverBloco, this)
        this.scope.onMoverBloco = this.action(this.#handleMoverBloco, this)
        this.scope.onSalvar = this.action(this.#handleSalvar, this)
        this.scope.onBuscarParceiro = this.action(this.#handleBuscarParceiro, this)
        this.scope.onSelecionarParceiro = this.action(this.#handleSelecionarParceiro, this)
        this.scope.onEnviarTeste = this.action(this.#handleEnviarTeste, this)
        this.scope.onAbrirConfirmacao = this.action(this.#handleAbrirConfirmacao, this)
        this.scope.onFecharConfirmacao = this.action(this.#handleFecharConfirmacao, this)
        this.scope.onDisparar = this.action(this.#handleDisparar, this)
        this.scope.podeGerirTemplate = podeGerirTemplateDaCampanha()
        this.scope.onPublicoChange = this.action(this.#handlePublicoChange, this)
        this.scope.onAbrirExclusao = this.action(this.#handleAbrirExclusao, this)
        this.scope.onFecharExclusao = this.action(async () => {
            this.scope.exclusao = undefined
        }, this)
        this.scope.onExcluirTemplatesChange = this.action(async (excluir: boolean) => {
            this.scope.excluirTemplates = excluir
        }, this)
        this.scope.onConfirmarExclusao = this.action(this.#handleConfirmarExclusao, this)
        this.scope.onCriarTemplate = this.action(this.#handleCriarTemplate, this)
        this.scope.onTemplateForaDaJanela = this.action(this.#handleTemplateForaDaJanela, this)
        this.scope.onAtualizarTemplate = this.action(this.#handleAtualizarTemplate, this)
        this.scope.onPausar = this.action(this.#handlePausar, this)
        this.scope.onRetomar = this.action(this.#handleRetomar, this)
        this.scope.onAtualizarSituacao = this.action(this.#handleAtualizarSituacao, this)
        this.scope.onBaixarRelatorio = this.action(this.#handleBaixarRelatorio, this)

        await Promise.all([this.#carregarLista(), this.#carregarParceiros()])
    }

    // :: Carga

    async #carregarLista() {
        this.scope.carregandoLista = true
        try {
            this.scope.campanhas = await service.listar()
        } catch (error) {
            this.unexpected('Carregando campanhas', error)
        } finally {
            this.scope.carregandoLista = false
        }
    }

    async #carregarParceiros(busca?: string) {
        this.scope.carregandoParceiros = true
        try {
            const parceiros = await service.listarParceiros(busca)

            // O selecionado precisa continuar entre as opções, senão o Autocomplete perde o valor.
            const selecionado = this.scope.parceiroTeste
            if (selecionado && !parceiros.some((item) => item.ownerId === selecionado.ownerId)) {
                parceiros.unshift(selecionado)
            }

            this.scope.parceiros = parceiros
        } catch (error) {
            this.unexpected('Carregando parceiros', error)
        } finally {
            this.scope.carregandoParceiros = false
        }
    }

    async #abrirCampanha(newsId: string) {
        try {
            const campanha = await service.obter(newsId)
            this.scope.campanha = campanha
            this.scope.newsId = campanha.newsId
            this.scope.blocos = await this.#comPreviews(campanha.blocos)
            this.scope.alterado = false
            this.scope.erroFormulario = undefined
            this.updateHistory()

            await Promise.all([this.#carregarAlcance(), this.#atualizarSituacao()])
        } catch (error) {
            this.unexpected('Abrindo a campanha', error)
        }
    }

    /** Busca o binário das imagens para a tela poder mostrá-las. */
    async #comPreviews(blocos: Bloco[]): Promise<Bloco[]> {
        const resultado: Bloco[] = []

        for (const bloco of blocos) {
            if (bloco.tipo !== 'imagem') {
                resultado.push(bloco)
                continue
            }

            try {
                const previewUrl = await service.baixarImagem(bloco.assetId)
                this.urlsCriadas.push(previewUrl)
                resultado.push({ ...bloco, previewUrl })
            } catch (error) {
                // Sem preview a campanha continua utilizável; só não mostra a miniatura.
                LOG.error(`Baixando a imagem ${bloco.assetId}`, error)
                resultado.push(bloco)
            }
        }

        return resultado
    }

    async #carregarAlcance() {
        const newsId = this.scope.campanha?.newsId
        if (!newsId) return

        this.scope.carregandoAlcance = true
        try {
            this.scope.alcance = await service.alcance(newsId)
        } catch (error) {
            this.unexpected('Calculando o alcance', error)
        } finally {
            this.scope.carregandoAlcance = false
        }
    }

    // :: Navegação

    async #handleTrocarAba(aba: Aba) {
        this.scope.aba = aba

        if (aba === 'disparo') {
            // O teste pode ter sido entregue enquanto a tela estava em outra aba.
            await this.#recarregarCampanha()
            await this.#carregarAlcance()
        }
        if (aba === 'situacao') {
            await this.#atualizarSituacao()
        } else {
            this.#pararPolling()
        }
    }

    async #handleNovaCampanha() {
        this.#pararPolling()
        this.scope.campanha = undefined
        this.scope.newsId = ''
        this.scope.blocos = [{ tipo: 'texto', texto: '' }]
        this.scope.alcance = undefined
        this.scope.situacao = undefined
        this.scope.diagnostico = undefined
        this.scope.erroFormulario = undefined
        this.scope.alterado = false
        this.scope.aba = 'editor'
        this.updateHistory()
    }

    async #handleAbrirCampanha(newsId: string) {
        this.scope.aba = 'editor'
        await this.#abrirCampanha(newsId)
    }

    // :: Editor

    async #handleNewsIdChange(texto: string) {
        this.scope.newsId = texto
        this.scope.alterado = true
        this.scope.erroFormulario = undefined
    }

    async #handleAdicionarTexto() {
        this.scope.blocos = [...this.scope.blocos, { tipo: 'texto', texto: '' }]
        this.scope.alterado = true
    }

    async #handleSelecionarImagem(arquivo: File) {
        if (!MIMES_ACEITOS.includes(arquivo.type)) {
            this.scope.erroFormulario = 'A imagem precisa ser .jpg ou .png — é o que o WhatsApp aceita.'
            return
        }
        if (arquivo.size > TAMANHO_MAXIMO_BYTES) {
            this.scope.erroFormulario = `A imagem tem ${(arquivo.size / 1024 / 1024).toFixed(1)} MB; o limite é 5 MB.`
            return
        }

        this.scope.enviandoImagem = true
        this.scope.erroFormulario = undefined
        try {
            const { assetId } = await service.enviarImagem(arquivo, this.scope.campanha?.newsId)
            const previewUrl = URL.createObjectURL(arquivo)
            this.urlsCriadas.push(previewUrl)

            this.scope.blocos = [
                ...this.scope.blocos,
                {
                    tipo: 'imagem',
                    assetId,
                    arquivo: arquivo.name,
                    mimeType: arquivo.type,
                    byteSize: arquivo.size,
                    previewUrl
                }
            ]
            this.scope.alterado = true
        } catch (error) {
            this.unexpected('Enviando a imagem', error)
        } finally {
            this.scope.enviandoImagem = false
        }
    }

    async #handleAlterarBloco({ indice, bloco }: { indice: number; bloco: Bloco }) {
        const blocos = [...this.scope.blocos]
        blocos[indice] = bloco
        this.scope.blocos = blocos
        this.scope.alterado = true
    }

    async #handleRemoverBloco(indice: number) {
        this.scope.blocos = this.scope.blocos.filter((_, i) => i !== indice)
        this.scope.alterado = true
    }

    async #handleMoverBloco({ indice, direcao }: { indice: number; direcao: -1 | 1 }) {
        const destino = indice + direcao
        if (destino < 0 || destino >= this.scope.blocos.length) {
            return
        }

        const blocos = [...this.scope.blocos]
        const [movido] = blocos.splice(indice, 1)
        blocos.splice(destino, 0, movido)
        this.scope.blocos = blocos
        this.scope.alterado = true
    }

    async #handleSalvar() {
        const newsId = this.scope.newsId.trim()
        if (!newsId) {
            this.scope.erroFormulario = 'Dê um identificador à campanha (ex.: convite-webinar-2026-09).'
            return
        }

        const blocos = this.scope.blocos.filter((bloco) => bloco.tipo === 'imagem' || bloco.texto.trim().length > 0)
        if (blocos.length === 0) {
            this.scope.erroFormulario = 'A campanha precisa de ao menos uma mensagem.'
            return
        }

        this.scope.salvando = true
        try {
            const campanha = await service.salvar(newsId, blocos, this.scope.campanha?.ativa ?? true)
            this.scope.campanha = campanha
            this.scope.blocos = await this.#comPreviews(campanha.blocos)
            this.scope.alterado = false
            this.scope.erroFormulario = undefined
            this.updateHistory()

            await Promise.all([this.#carregarLista(), this.#carregarAlcance()])
            this.app.notify(
                'success',
                `Campanha ${campanha.newsId} salva. Nada foi enviado ainda — use a aba Testar e disparar.`
            )
        } catch (error) {
            this.unexpected('Salvando a campanha', error)
        } finally {
            this.scope.salvando = false
        }
    }

    // :: Disparo

    async #handleBuscarParceiro(texto: string) {
        this.#cancelarBuscaAgendada()
        this.buscaParceiroTimer = setTimeout(() => {
            this.buscaParceiroTimer = undefined
            this.#carregarParceiros(texto.trim() || undefined).catch((error) => LOG.error('Buscando parceiros', error))
        }, DEBOUNCE_BUSCA_MS)
    }

    #cancelarBuscaAgendada() {
        if (this.buscaParceiroTimer) {
            clearTimeout(this.buscaParceiroTimer)
            this.buscaParceiroTimer = undefined
        }
    }

    async #handleSelecionarParceiro(parceiro?: Parceiro) {
        this.scope.parceiroTeste = parceiro
        this.scope.diagnostico = undefined

        if (!parceiro) return

        this.scope.carregandoDiagnostico = true
        try {
            this.scope.diagnostico = await service.diagnosticar(parceiro.ownerId, this.scope.campanha?.newsId)
        } catch (error) {
            this.unexpected('Conferindo o parceiro', error)
        } finally {
            this.scope.carregandoDiagnostico = false
        }
    }

    async #handleEnviarTeste() {
        const newsId = this.scope.campanha?.newsId
        const parceiro = this.scope.parceiroTeste
        if (!newsId || !parceiro) return

        this.scope.enviandoTeste = true
        try {
            const resultado = await service.enviarTeste(newsId, parceiro.ownerId)
            if (resultado.enfileirados > 0) {
                this.app.notify('success', `Teste enviado para ${parceiro.nome}. Confirme com ele o que chegou.`)
            } else {
                this.app.notify('warning', 'Nada foi enfileirado — confira os impedimentos acima.')
            }

            // O disparo geral depende do teste ENTREGUE, e a entrega acontece na fila um
            // instante depois. Perguntar só agora devolveria "não testada" sempre.
            await this.#recarregarCampanha()
            await this.#atualizarSituacao()
            this.#aguardarEntregaDoTeste()
        } catch (error) {
            this.unexpected('Enviando o teste', error)
        } finally {
            this.scope.enviandoTeste = false
        }
    }

    /**
     * Reconsulta até a entrega do teste ser confirmada (é o que libera o disparo geral).
     * Desiste em silêncio depois de ESPERA_TESTE_MAX_TENTATIVAS — o operador ainda pode
     * reentrar na aba para reconsultar.
     */
    #aguardarEntregaDoTeste(tentativa = 1) {
        this.#pararEsperaDoTeste()

        if (this.scope.campanha?.testada) {
            this.scope.aguardandoTeste = false
            return
        }
        if (tentativa > ESPERA_TESTE_MAX_TENTATIVAS) {
            this.scope.aguardandoTeste = false
            return
        }

        this.scope.aguardandoTeste = true
        this.testeTimer = setTimeout(() => {
            this.testeTimer = undefined
            this.#recarregarCampanha()
                .then(() => this.#aguardarEntregaDoTeste(tentativa + 1))
                .catch((error) => {
                    this.scope.aguardandoTeste = false
                    LOG.error('Aguardando a entrega do teste', error)
                })
        }, ESPERA_TESTE_MS)
    }

    #pararEsperaDoTeste() {
        if (this.testeTimer) {
            clearTimeout(this.testeTimer)
            this.testeTimer = undefined
        }
    }

    async #handleAbrirConfirmacao() {
        await this.#carregarAlcance()
        this.scope.confirmacaoAberta = true
    }

    async #handleFecharConfirmacao() {
        this.scope.confirmacaoAberta = false
    }

    async #handleDisparar() {
        const newsId = this.scope.campanha?.newsId
        if (!newsId) return

        this.scope.disparando = true
        try {
            const resultado = await service.disparar(newsId)
            this.scope.confirmacaoAberta = false
            this.app.notify('success', `Campanha disparada. Enfileirados agora: ${resultado.enfileirados}.`)

            this.scope.aba = 'situacao'
            await this.#recarregarCampanha()
            await this.#atualizarSituacao()
        } catch (error) {
            this.unexpected('Disparando a campanha', error)
        } finally {
            this.scope.disparando = false
        }
    }

    // :: Exclusão

    async #handleAbrirExclusao(newsId: string) {
        try {
            this.scope.exclusao = await service.previaDaExclusao(newsId)
            this.scope.excluirTemplates = false
        } catch (error) {
            this.unexpected('Preparando a exclusão da campanha', error)
        }
    }

    async #handleConfirmarExclusao() {
        const previa = this.scope.exclusao
        if (!previa || previa.impedimento || this.scope.excluindo) return

        this.scope.excluindo = true
        try {
            const r = await service.excluir(previa.newsId, this.scope.excluirTemplates && this.scope.podeGerirTemplate)
            this.scope.exclusao = undefined
            if (this.scope.campanha?.newsId === previa.newsId) await this.#handleNovaCampanha()
            await this.#carregarLista()

            const templates = r.templatesExcluidos.length
                ? ` Templates excluídos: ${r.templatesExcluidos.join(', ')}.`
                : ''
            this.app.notify('success', `Campanha ${r.newsId} excluída.${templates}`, 6000)
            if (r.templatesNaoExcluidos.length) {
                this.app.alert(
                    'warning',
                    'Alguns templates ficaram na conta',
                    r.templatesNaoExcluidos.map((t) => `${t.nome}: ${t.motivo}`).join('\n')
                )
            }
        } catch (error) {
            if (error instanceof ServiceError && error.statusCode === 400) {
                this.app.alert('warning', 'Exclusão da campanha', error.message)
            } else {
                this.unexpected('Excluindo a campanha', error)
            }
        } finally {
            this.scope.excluindo = false
        }
    }

    // :: Template da campanha (E29)

    async #handleCriarTemplate() {
        const newsId = this.scope.campanha?.newsId
        if (!newsId || this.scope.salvandoTemplate) return

        this.scope.salvandoTemplate = true
        try {
            this.scope.campanha = await service.criarTemplate(newsId)
            const nome = this.scope.campanha.template.nome
            this.app.notify('success', `Template ${nome} enviado à Meta. A aprovação leva de minutos a horas.`, 6000)
        } catch (error) {
            this.#erroDoTemplate('Criando o template da campanha', error)
        } finally {
            this.scope.salvandoTemplate = false
        }
    }

    async #handleTemplateForaDaJanela(ligado: boolean) {
        const newsId = this.scope.campanha?.newsId
        if (!newsId || this.scope.salvandoTemplate) return

        this.scope.salvandoTemplate = true
        try {
            this.scope.campanha = await service.templateForaDaJanela(newsId, ligado)
            await this.#carregarAlcance()
        } catch (error) {
            this.#erroDoTemplate('Mudando o envio do template', error)
        } finally {
            this.scope.salvandoTemplate = false
        }
    }

    async #handlePublicoChange(valor: string) {
        const newsId = this.scope.campanha?.newsId
        if (!newsId || this.scope.salvandoTemplate) return

        this.scope.salvandoTemplate = true
        try {
            this.scope.campanha = await service.definirPublico(newsId, valor ? Number(valor) : null)
            await this.#carregarAlcance()
        } catch (error) {
            this.#erroDoTemplate('Mudando o público do template', error)
        } finally {
            this.scope.salvandoTemplate = false
        }
    }

    /** A aprovação chega pela Meta a qualquer momento: a tela reconsulta quando pedem. */
    async #handleAtualizarTemplate() {
        try {
            await this.#recarregarCampanha()
            await this.#carregarAlcance()
        } catch (error) {
            this.unexpected('Atualizando o template da campanha', error)
        }
    }

    #erroDoTemplate(contexto: string, error: unknown) {
        if (error instanceof ServiceError && error.statusCode === 400) {
            this.app.alert('warning', 'Template da campanha', error.message)
            return
        }
        this.unexpected(contexto, error)
    }

    async #handlePausar() {
        const newsId = this.scope.campanha?.newsId
        if (!newsId) return

        try {
            this.scope.campanha = await service.pausar(newsId)
            this.app.notify('info', 'Campanha pausada: ninguém mais recebe até ser reativada.')
            await this.#carregarLista()
        } catch (error) {
            this.unexpected('Pausando a campanha', error)
        }
    }

    async #handleRetomar() {
        const newsId = this.scope.campanha?.newsId
        if (!newsId) return

        try {
            this.scope.campanha = await service.retomar(newsId)
            this.app.notify('info', 'Campanha reativada.')
            await this.#carregarLista()
        } catch (error) {
            this.unexpected('Reativando a campanha', error)
        }
    }

    // :: Situação

    async #handleAtualizarSituacao() {
        await this.#atualizarSituacao()
    }

    async #handleBaixarRelatorio() {
        const newsId = this.scope.campanha?.newsId
        if (!newsId) return

        this.scope.baixandoRelatorio = true
        try {
            const { nome, blob } = await service.baixarRelatorio(newsId)
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = nome
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
        } catch (error) {
            this.unexpected('Baixando o relatório', error)
        } finally {
            this.scope.baixandoRelatorio = false
        }
    }

    async #recarregarCampanha() {
        const newsId = this.scope.campanha?.newsId
        if (!newsId) return

        this.scope.campanha = await service.obter(newsId)
        await this.#carregarLista()
    }

    async #atualizarSituacao() {
        const newsId = this.scope.campanha?.newsId
        if (!newsId) return

        this.scope.carregandoSituacao = true
        try {
            const situacao = await service.situacao(newsId)
            this.scope.situacao = situacao

            // Só continua perguntando enquanto há entrega em andamento e a aba está aberta.
            const pendentes = (situacao.porStatus.enqueued ?? 0) + (situacao.fila.pending ?? 0)
            if (this.scope.aba === 'situacao' && pendentes > 0) {
                this.#agendarPolling()
            } else {
                this.#pararPolling()
            }
        } catch (error) {
            this.unexpected('Atualizando a situação', error)
        } finally {
            this.scope.carregandoSituacao = false
        }
    }

    #agendarPolling() {
        this.#pararPolling()
        this.pollingTimer = setTimeout(() => {
            this.pollingTimer = undefined
            this.#atualizarSituacao().catch((error) => LOG.error('Atualizando a situação', error))
        }, POLLING_MS)
    }

    #pararPolling() {
        if (this.pollingTimer) {
            clearTimeout(this.pollingTimer)
            this.pollingTimer = undefined
        }
    }
}

export default CampanhaPresenter
